import type { SortDirection } from '@ai-meeting/shared'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'

export interface AdminColumn<TRow, TSortBy extends string> {
  key: string
  label: string
  sortBy?: TSortBy
  align?: 'left' | 'right'
  cellClassName?: string
  render: (row: TRow) => React.ReactNode
}

interface AdminDataTableProps<TRow, TSortBy extends string> {
  columns: AdminColumn<TRow, TSortBy>[]
  rows: TRow[]
  rowKey: (row: TRow) => string
  loading: boolean
  error: string | null
  sortBy: TSortBy
  sortDir: SortDirection
  onToggleSort: (column: TSortBy) => void
  page: number
  totalPages: number
  total: number
  onPageChange: (page: number) => void
}

/**
 * Presentational, sortable + paginated table shared by every admin tab. The
 * table lives in its own horizontal-scroll container so wide column sets never
 * push the page body sideways. Sort state and data are owned by
 * `useAdminTable`; this component only renders.
 */
export default function AdminDataTable<TRow, TSortBy extends string>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  sortBy,
  sortDir,
  onToggleSort,
  page,
  totalPages,
  total,
  onPageChange,
}: AdminDataTableProps<TRow, TSortBy>) {
  const { t } = useUiStrings()

  return (
    <div className="w-full min-w-0">
      <div className="w-full overflow-x-auto rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)]">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead>
            <tr className="border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]">
              {columns.map((col) => {
                const sortable = Boolean(col.sortBy)
                const active = sortable && col.sortBy === sortBy
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={
                      active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={[
                      'whitespace-nowrap px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]',
                      col.align === 'right' ? 'text-right' : 'text-left',
                    ].join(' ')}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onToggleSort(col.sortBy as TSortBy)}
                        className={[
                          'inline-flex items-center gap-1 transition-colors hover:text-[var(--text-primary)]',
                          col.align === 'right' ? 'flex-row-reverse' : '',
                        ].join(' ')}
                      >
                        {col.label}
                        <span aria-hidden="true" className="text-[10px] opacity-70">
                          {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className="border-b border-[var(--border-color)] last:border-b-0 hover:bg-[var(--bg-tertiary)]"
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={[
                      'px-3 py-2.5 text-[var(--text-primary)]',
                      col.align === 'right' ? 'text-right tabular-nums' : 'text-left',
                      col.cellClassName ?? 'whitespace-nowrap',
                    ].join(' ')}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-10 text-center text-sm text-[var(--text-tertiary)]"
                >
                  {error ?? t.adminEmpty}
                </td>
              </tr>
            )}
            {loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-10 text-center text-sm text-[var(--text-tertiary)]"
                >
                  {t.loading}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--text-secondary)]">
        <span>{fmt(t.adminTotalRows, { total })}</span>
        <div className="flex items-center gap-3">
          <span className="tabular-nums">{fmt(t.adminPageInfo, { page, totalPages })}</span>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || loading}
              className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-1.5 font-semibold transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-40 disabled:hover:bg-[var(--bg-secondary)]"
            >
              {t.adminPrevPage}
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || loading}
              className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-1.5 font-semibold transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-40 disabled:hover:bg-[var(--bg-secondary)]"
            >
              {t.adminNextPage}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
